import { CommonModule } from '@angular/common';
import { Component, ElementRef, Signal, ViewChild, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StationLongDto } from '@api/rest';
import { AppService } from '@app/core/services/app.service';
import { StationType } from '@app/shared/enums/station-type.enum';
import { StationFacade } from '@app/station/station.facade';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ImprintComponent } from '../imprint/imprint.component';
import { AgeGroup, Avatar, Goal, ProfileData, Question } from '@app/shared/models/form-profile';
import { form, FormField } from '@angular/forms/signals';
import { v4 as uuidv4 } from 'uuid';
import { Child } from '@app/shared/models/child';
import { TourFacade } from '@app/tour/tour.facade';
import { InstructionComponent } from "../instruction/instruction.component";

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    imports: [
    CommonModule,
    MatCheckboxModule,
    MatExpansionModule,
    FormsModule,
    MatButtonModule,
    ImprintComponent,
    TranslatePipe,
    FormField,
    InstructionComponent
],
    standalone: true
})
export class ProfileComponent {
  private readonly router = inject(Router);
  private readonly appService = inject(AppService);
  private readonly facade = inject(StationFacade);
  private readonly tourFacade = inject(TourFacade);
  private readonly translate = inject(TranslateService);
  private readonly childrenSignal = this.appService.childrenSignal;
  private readonly selectedChild = this.appService.selectedChild;
  private readonly readInstructionsSignal = signal(false);

  private profileModel = signal<ProfileData>({
    acceptTerms: this.appService.acceptedEulaSignal(),
    readInstructions: this.readInstructionsSignal(),
    ageGroup: this.selectedChild()?.ageGroup
      ? this.selectedChild()?.ageGroup
      : AgeGroup.tenToFourteen,
    avatar: this.selectedChild()?.avatar
      ? this.selectedChild()?.avatar
      : Avatar.bear,
    name: this.selectedChild()?.name
      ? this.selectedChild().name
      :  this.translate.instant('Kind') + ' ' + ((this.childrenSignal()?.length + 1) || 1),
    selectedSituations: this.selectedChild() ? this.selectedChild().selectedSituations : [],
    noMatching: false,
    goals: [],
    goal: Goal.general
  });


  protected readonly eulaExpanded = signal(false);
  protected readonly stations$ = this.facade.getStationsByType(StationType.QUESTION);
  protected readonly page = signal(-2);
  protected readonly questionsPerPage = 4;
  protected readonly profileForm = form(this.profileModel);
  protected readonly availableAvatars = [Avatar.bear, Avatar.bunny, Avatar.cat, Avatar.ladybug, Avatar.pig];
  protected readonly availableMainGoals = [
    Goal.familyAndSocial,
    Goal.reflectedDecisionsRegulatedEmotions,
    Goal.dailyJoyMoreInterest,
    Goal.enjoyHealthyFood
  ];
  @ViewChild('popoverDialog') popover!: ElementRef;
  @ViewChild('popoverMainGoalDialog') popoverMainGoal!: ElementRef;

  protected isLastSlide = computed(() => {
    return this.page() - 1 >= (this.questionsAsSignal()?.length / this.questionsPerPage);
  });

  // Get profile questions for correct area
  protected readonly questionStationsAsSignal = computed(() => {
    const allStations = this.facade.allStationsSignal();
    return this.tourFacade.profileTourSignal().station_refs
    .map(stationId => allStations.find(station => station.id === stationId));
  });

  protected readonly questionsAsSignal: Signal<Question[]> = computed(() => {
    return this.questionStationsAsSignal().map((station, index) => {
      const goalToReturn: Question = { station: station, goal: this.getGoalWithIndex(index)};
      return goalToReturn;
    })
  });

  protected readonly overallSelectedGoals: Signal<Goal[]> = computed(() => {
    const overallGoals: Goal[] = [];
    this.profileForm.goals().value().forEach(goal => {
      if (!overallGoals.includes(goal.goal)) {
        overallGoals.push(goal.goal);
      }
    });
    return overallGoals;
  });

  protected currentPageQuestions = computed(() => {
    const questions = this.questionsAsSignal();
    const page = this.page() - 1;
    const perPage = this.questionsPerPage;

    const start = page * perPage;
    const end = start + perPage;

    const pageQuestions = questions.slice(start, end);

    return this.shuffle([...pageQuestions]);
  });

  private showMainGoalDialog() {
    const popoverMainGoal = this.popoverMainGoal.nativeElement;
    popoverMainGoal.showPopover();
  }

  private getGoalIsClear: Signal<boolean> = computed(() => {
    const goals = this.profileForm.goals().value();
    if (goals?.length === 1) {
      return true;
    } else {
      return false;
    }
  });

  protected goToNext() {
    this.page.update(val => val + 1);
    window.scroll(0, 0);
    this.profileModel.update(model => {
      return {
        ...model,
        noMatching: false
      };
    })

    if (this.isLastSlide()) {
      if (!this.appService.acceptedEulaSignal()) {
        this.appService.setAcceptedEula(true);
      }

      if (this.profileForm.selectedSituations().value()?.length > 0) {
        const clearGoal = this.getGoalIsClear();
        if (clearGoal) {
          this.profileModel.update(model => ({
            ...model,
            goal: model.goals?.[0].goal ? model.goals[0].goal : Goal.general
          }));

          this.persistFormIntoChild();
          this.router.navigate(['child-admin']);
        } else {
          this.showMainGoalDialog();
        }
      } else {
        this.showNoSelectionPopover();
      }

    }
  }

  private getGoalWithIndex(index: number): Goal {
    if ((index % this.questionsPerPage) === 0) {
      return Goal.familyAndSocial;
    } else if ((index % this.questionsPerPage) === 1) {
      return Goal.reflectedDecisionsRegulatedEmotions;
    } else if ((index % this.questionsPerPage) === 2) {
      return Goal.dailyJoyMoreInterest;
    } else if ((index % this.questionsPerPage) === 3) {
      return Goal.enjoyHealthyFood;
    } else {
      return Goal.general;
    }
  }

  private shuffle<T>(arr: T[]) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  private persistFormIntoChild() {
    console.log('persistFormIntoChild', this.profileForm.goal().value());
    const childToAdd: Child = {
      id: this.selectedChild()?.id ? this.selectedChild().id : uuidv4(),
      name: this.profileForm.name().value(),
      avatar: this.profileForm.avatar().value(),
      ageGroup: this.profileForm.ageGroup().value(),
      selectedSituations: this.profileForm.selectedSituations().value(),
      goal: this.profileForm.goal().value()
    }

    this.appService.addChild(childToAdd);
    this.appService.selectChild(childToAdd.id);
  }

  protected skipCheckin() {
    if (this.appService.firstStartSignal()) {
      this.router.navigate(['tutorial']);
    } else {
      this.router.navigate(['exhibition']);
    }
    this.appService.setAcceptedEula(true);
  }

  protected progress = computed(() => {
    return map(() => Math.round(((this.page() * this.questionsPerPage) / this.questionsAsSignal()?.length) * 100))
  });

  protected pageNumber = computed(() => {
    return Math.round(this.questionsAsSignal()?.length / this.questionsPerPage);
  })

  protected getTextFromStation(stationId: string): Observable<string> {
   return this.stations$
      .pipe(
        map(
          (stations: StationLongDto[]) => stations
            .find(station => station.id === stationId)?.textlong[this.translate.getCurrentLang()]
          )
        );
  }

  protected isSituationSelected(situation: string): boolean {
    return this.profileModel().selectedSituations.includes(situation);
  }

  protected toggleSituation(isChecked: boolean, situation: Question, page: number) {
    this.profileModel.update(model => {
      const exists = model.selectedSituations.includes(situation.station.id);


      const updatedSelectedSituations = exists
      ? model.selectedSituations.filter(s => s !== situation.station.id)
      : [...model.selectedSituations, situation.station.id];

      let updatedGoals = [...model.goals];

      if (isChecked) {
        // add goal if not already present for this page
        if (!model.goals.some(goal => goal.goal === situation.goal && goal.page === page)) {
          updatedGoals = [...model.goals, { page, goal: situation.goal }];
        }
      } else {
        // remove goal for this page/goal when unchecked (including last element)
        updatedGoals = model.goals.filter(
          goal => !(goal.goal === situation.goal && goal.page === page)
        );
      }

      return {
        ...model,
        selectedSituations: updatedSelectedSituations,
        noMatching: updatedSelectedSituations?.length === 0,
        goals: updatedGoals
      };
    });
  }

  protected toggleCurrentPageSituations(page: number): void {
    this.profileModel.update(model => {
      return {
        ...model,
        noMatching: !model.noMatching
      }
    });

    if (this.profileForm.noMatching().value()) {
      const start = (page - 1) * this.questionsPerPage;
      const end = start + this.questionsPerPage;

      const pageQuestionIds = this.questionsAsSignal()
        .slice(start, end)
        .map(q => q.station.id);

      this.profileModel.update(model => ({
        ...model,
        selectedSituations: model.selectedSituations.filter(
          id => !pageQuestionIds.includes(id)
        )
      }));
    }
  }

  protected toggleAcceptTerms() {
    this.profileModel.update(model => {
      return {
        ...model,
        acceptTerms: !model.acceptTerms
      }
    });
  }

  protected toggleAcceptReadInstructions() {
    this.profileModel.update(model => {
      return {
        ...model,
        readInstructions: !model.readInstructions
      }
    });
  }

  protected onClickContinueSelectMainGoal() {
    this.persistFormIntoChild();
    this.popoverMainGoal.nativeElement.hidePopover();
    this.router.navigate(['child-admin']);
  }

  private showNoSelectionPopover() {
    const popoverElement = this.popover.nativeElement;
    popoverElement.showPopover();
  }

  private hideNoSelectionPopover() {
    const popoverElement = this.popover.nativeElement;
    popoverElement.hidePopover();
  }

  protected onDialogContinue() {
    this.persistFormIntoChild();
    this.router.navigate(['child-admin']);
  }

  protected onDialogBack() {
    this.page.set(0);
    this.hideNoSelectionPopover();
  }
}
